import React from 'react';
import { MapPin, Users, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getGroupedRegistrations, RegistrationSummary } from '../services/alerts';

export function RegistrationsList() {
  const [registrations, setRegistrations] = React.useState<RegistrationSummary[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = React.useState<RegistrationSummary[]>([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    loadRegistrations();
  }, []);

  React.useEffect(() => {
    if (searchTerm) {
      const filtered = registrations.filter(reg => 
        reg.origin_city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reg.destination_cities.some(city => 
          city.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredRegistrations(filtered);
    } else {
      setFilteredRegistrations(registrations);
    }
  }, [searchTerm, registrations]);

  const loadRegistrations = async () => {
    try {
      const data = await getGroupedRegistrations();
      setRegistrations(data);
      setFilteredRegistrations(data);
      setError(null);
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar registros');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Carregando registros...</div>;
  }

  if (error) {
    return <div className="p-4 bg-red-100 text-red-700 rounded-lg">{error}</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <Users className="text-blue-600" size={24} />
        <h2 className="text-xl font-semibold text-gray-900">
          Registros de Alertas
        </h2>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por origem ou destino..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {filteredRegistrations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Nenhum registro encontrado
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRegistrations.map((registration, index) => (
            <div
              key={`${registration.origin_city}-${index}`}
              className="bg-gray-50 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin size={18} className="text-gray-400" />
                    <span className="font-medium text-gray-900">
                      {registration.origin_city}
                    </span>
                  </div>
                  
                  <div className="pl-6">
                    <div className="text-sm text-gray-600 mb-2">Destinos:</div>
                    <div className="flex flex-wrap gap-2">
                      {registration.destination_cities.map((city, cityIndex) => (
                        <span
                          key={cityIndex}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {city}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="ml-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {registration.registration_count} {registration.registration_count === 1 ? 'registro' : 'registros'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}